import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/Auth';
import { Layout } from './components/Layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { TodoPage } from './pages/TodoPage';
import { VenuesPage } from './pages/VenuesPage';
import { InboxPage } from './pages/InboxPage';
import { ImportPage } from './pages/ImportPage';
import { ProfilePage } from './pages/ProfilePage';
import { MailingListsPage } from './pages/MailingListsPage';
import { AdminStatsPage } from './pages/AdminStatsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { CoreVenuesPage } from './pages/CoreVenuesPage';
import { CorrectionsPage } from './pages/CorrectionsPage';
import { DisambiguatePage } from './pages/DisambiguatePage';
import { AdminSecurityPage } from './pages/AdminSecurityPage';
import { ConcertsPage } from './pages/ConcertsPage';
import { ContactPage } from './pages/ContactPage';
import { TermsPage } from './pages/TermsPage';
import { FAQPage } from './pages/FAQPage';
import { useAuth } from './contexts/AuthContext';
import { SmartToaster } from './components/Common/SmartToaster';

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/todo" replace />;
  return <>{children}</>;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SmartToaster />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/callback" element={<AuthCallbackPage />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/todo" replace />} />
                      <Route path="/todo" element={<TodoPage />} />
                      <Route path="/venues" element={<VenuesPage />} />
                      <Route path="/concerts" element={<ConcertsPage />} />
                      <Route path="/mailing-lists" element={<MailingListsPage />} />
                      <Route path="/inbox" element={<InboxPage />} />
                      <Route path="/import" element={<ImportPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/admin" element={<Navigate to="/admin/stats" replace />} />
                      <Route path="/admin/stats" element={<AdminRoute><AdminStatsPage /></AdminRoute>} />
                      <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                      <Route path="/admin/core-venues" element={<AdminRoute><CoreVenuesPage /></AdminRoute>} />
                      <Route path="/admin/corrections" element={<AdminRoute><CorrectionsPage /></AdminRoute>} />
                      <Route path="/admin/disambiguate" element={<AdminRoute><DisambiguatePage /></AdminRoute>} />
                      <Route path="/admin/security" element={<AdminRoute><AdminSecurityPage /></AdminRoute>} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
